import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Wallet, Plus, Building2, AlertCircle, ChevronRight, Search, Loader2, RefreshCw, Crown,
} from 'lucide-react';
import { toast } from 'sonner';
import MembershipAccountsTab from './MembershipAccounts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtAUD = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n || 0);

export default function Accounts() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState('memberships');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (!r.ok) { navigate('/login'); return; }
        const u = await r.json();
        if (u.role !== 'super_admin' && u.role !== 'admin') {
          toast.error('Owner access only');
          navigate('/');
          return;
        }
        setUser(u);
      } catch { navigate('/login'); }
    })();
  }, [navigate]);

  if (!user) return null;

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 lg:p-8" data-testid="accounts-page">
        <div className="mb-5">
          <h1 className="font-['Chivo'] font-bold text-2xl sm:text-3xl text-slate-900">Accounts</h1>
          <p className="text-slate-500 text-sm">Membership subscriptions & 30-day B2B credit accounts</p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="memberships" data-testid="tab-membership-accounts">
              <Crown className="h-4 w-4 mr-2" /> Membership Accounts
            </TabsTrigger>
            <TabsTrigger value="thirty-day" data-testid="tab-30day-accounts">
              <Wallet className="h-4 w-4 mr-2" /> 30 Day Accounts
            </TabsTrigger>
          </TabsList>
          <TabsContent value="memberships">
            <MembershipAccountsTab />
          </TabsContent>
          <TabsContent value="thirty-day">
            <ThirtyDayAccounts />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function ThirtyDayAccounts() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');

  const refresh = async () => {
    setLoading(true);
    try {
      const [aRes, dRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/accounts${search ? `?search=${encodeURIComponent(search)}` : ''}`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/accounts/dashboard`, { credentials: 'include' }),
      ]);
      if (aRes.ok) setAccounts(await aRes.json());
      if (dRes.ok) setDashboard(await dRes.json());
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
   
  useEffect(() => { refresh(); }, [search]);

  return (
    <div data-testid="thirty-day-accounts-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-slate-500 text-sm">Manage credit terms, monthly statements & receivables</p>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowCreate(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="new-account-btn">
            <Plus className="h-4 w-4 mr-2" /> New Account
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const r = await fetch(`${BACKEND_URL}/api/accounts/sync-orphan-invoices`, {
                  method: 'POST', credentials: 'include',
                });
                if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
                const data = await r.json();
                toast.success(data.message || `Stamped ${data.updated} invoice(s).`);
                refresh();
              } catch (e) { toast.error(e.message || 'Sync failed'); }
            }}
            data-testid="sync-orphan-invoices-btn"
            title="Find invoices that should be on a credit account but aren't, and stamp them so they appear here and on month-end statements."
          >
            <RefreshCw className="h-4 w-4 mr-2" /> Sync orphan invoices
          </Button>
        </div>
      </div>

      {/* KPI tiles */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Kpi label="Active accounts" value={dashboard.active_accounts} tone="emerald" />
          <Kpi label="Suspended" value={dashboard.suspended_accounts} tone={dashboard.suspended_accounts ? 'amber' : 'slate'} />
          <Kpi label="Total outstanding" value={fmtAUD(dashboard.total_outstanding)} tone="blue" />
          <Kpi label="Overdue" value={fmtAUD(dashboard.overdue_amount)} tone={dashboard.overdue_amount > 0 ? 'rose' : 'slate'} />
          <Kpi label="Open invoices" value={dashboard.open_invoices_count} />
        </div>
      )}

      <Card className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <Search className="h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name / code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-md h-9"
            data-testid="account-search"
          />
        </div>

        {loading ? (
          <div className="py-12 text-center text-slate-500"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
        ) : accounts.length === 0 ? (
          <div className="py-16 text-center text-slate-500">
            <Building2 className="h-12 w-12 mx-auto mb-3 opacity-40" />
            No credit accounts yet. Create one for your B2B customers.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Account</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-right">Credit terms</TableHead>
                <TableHead className="text-right">Limit</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead className="text-right">Overdue</TableHead>
                <TableHead className="text-right">Open</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.map((a) => (
                <TableRow key={a.account_id} className="cursor-pointer hover:bg-slate-50"
                  onClick={() => navigate(`/accounts/${a.account_id}`)}
                  data-testid="account-row">
                  <TableCell>
                    <p className="font-medium text-slate-900">{a.account_name}</p>
                    {a.account_code && <p className="text-xs text-slate-500 font-mono">{a.account_code}</p>}
                  </TableCell>
                  <TableCell className="text-slate-700">{a.customer?.display_name || '—'}</TableCell>
                  <TableCell className="text-center">
                    <StatusBadge status={a.status} />
                  </TableCell>
                  <TableCell className="text-right">Net {a.credit_terms_days}d</TableCell>
                  <TableCell className="text-right">{a.credit_limit ? fmtAUD(a.credit_limit) : '—'}</TableCell>
                  <TableCell className="text-right font-semibold text-slate-900">{fmtAUD(a.current_balance)}</TableCell>
                  <TableCell className="text-right">
                    {a.overdue_amount > 0
                      ? <span className="text-rose-600 font-semibold flex items-center justify-end gap-1"><AlertCircle className="h-3.5 w-3.5" />{fmtAUD(a.overdue_amount)}</span>
                      : '—'}
                  </TableCell>
                  <TableCell className="text-right">{a.open_invoices_count}</TableCell>
                  <TableCell><ChevronRight className="h-4 w-4 text-slate-400" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <CreateAccountDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={refresh} />
    </div>
  );
}

function Kpi({ label, value, tone = 'slate' }) {
  const tones = {
    slate: 'text-slate-900', emerald: 'text-emerald-600',
    blue: 'text-blue-600', amber: 'text-amber-600', rose: 'text-rose-600',
  };
  return (
    <Card className="p-4">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-2xl font-semibold mt-1 ${tones[tone]}`}>{value ?? '—'}</p>
    </Card>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: 'bg-emerald-100 text-emerald-700',
    suspended: 'bg-amber-100 text-amber-700',
    closed: 'bg-slate-200 text-slate-700',
  };
  return <Badge className={`${map[status] || ''} hover:${map[status] || ''}`}>{status}</Badge>;
}

function CreateAccountDialog({ open, onClose, onCreated }) {
  const [submitting, setSubmitting] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [form, setForm] = useState({
    account_name: '', account_code: '', credit_terms_days: 30,
    credit_limit: '', contact_email: '', contact_phone: '', notes: '',
  });

  useEffect(() => {
    if (!customerSearch || customerSearch.length < 2) { setCustomerResults([]); return; }
    const t = setTimeout(async () => {
      const r = await fetch(`${BACKEND_URL}/api/customers?page=1&page_size=8&search=${encodeURIComponent(customerSearch)}`, { credentials: 'include' });
      if (r.ok) { const d = await r.json(); setCustomerResults(d.customers || []); }
    }, 250);
    return () => clearTimeout(t);
  }, [customerSearch]);

  const reset = () => {
    setSelectedCustomer(null);
    setCustomerSearch('');
    setCustomerResults([]);
    setForm({ account_name: '', account_code: '', credit_terms_days: 30, credit_limit: '', contact_email: '', contact_phone: '', notes: '' });
  };
  const close = () => { reset(); onClose(); };

  const submit = async () => {
    if (!selectedCustomer) return toast.error('Pick a customer first');
    if (!form.account_name.trim()) return toast.error('Account name required');
    setSubmitting(true);
    try {
      const payload = {
        customer_id: selectedCustomer.customer_id,
        account_name: form.account_name.trim(),
        account_code: form.account_code.trim() || null,
        credit_terms_days: parseInt(form.credit_terms_days || 30, 10),
        credit_limit: form.credit_limit === '' ? null : parseFloat(form.credit_limit),
        contact_email: form.contact_email.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
        notes: form.notes.trim() || null,
        statement_day: 30,
        payment_due_day: 10,
      };
      const r = await fetch(`${BACKEND_URL}/api/accounts`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      toast.success(`Account "${payload.account_name}" created`);
      onCreated();
      close();
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-2xl" data-testid="create-account-dialog">
        <DialogHeader>
          <DialogTitle>New B2B Credit Account</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Customer</Label>
            {selectedCustomer ? (
              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <div>
                  <p className="font-medium">{selectedCustomer.display_name}</p>
                  <p className="text-sm text-slate-600">{selectedCustomer.mobile || selectedCustomer.phone || '—'} · {selectedCustomer.suburb || '—'}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedCustomer(null)}>Change</Button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  placeholder="Search existing customer by name / company / mobile…"
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  data-testid="customer-search-input"
                />
                {customerResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-72 overflow-auto">
                    {customerResults.map((c) => (
                      <button key={c.customer_id}
                        onClick={() => { setSelectedCustomer(c); setForm((f) => ({ ...f, account_name: f.account_name || c.company || c.display_name })); setCustomerResults([]); }}
                        className="w-full text-left p-3 hover:bg-slate-50 border-b last:border-0">
                        <p className="font-medium">{c.display_name}</p>
                        <p className="text-xs text-slate-500">{c.mobile || c.phone || '—'} · {c.suburb || '—'}</p>
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  Don't see them? <a href="/customers" className="text-blue-600 underline">Create the customer first</a>, then come back.
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Account name *</Label>
              <Input value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} placeholder="Melbourne Collision Repairs" />
            </div>
            <div>
              <Label>Account code</Label>
              <Input value={form.account_code} onChange={(e) => setForm({ ...form, account_code: e.target.value })} placeholder="MCR001" className="font-mono" />
            </div>
            <div>
              <Label>Credit terms (days) *</Label>
              <Select value={String(form.credit_terms_days)} onValueChange={(v) => setForm({ ...form, credit_terms_days: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Net 7 days</SelectItem>
                  <SelectItem value="14">Net 14 days</SelectItem>
                  <SelectItem value="30">Net 30 days (default)</SelectItem>
                  <SelectItem value="45">Net 45 days</SelectItem>
                  <SelectItem value="60">Net 60 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Credit limit (AUD)</Label>
              <Input type="number" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} placeholder="Optional" />
            </div>
            <div>
              <Label>Statement contact email</Label>
              <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
            </div>
            <div>
              <Label>Contact phone</Label>
              <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Internal notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything to remember about this account" />
            </div>
          </div>

          <p className="text-xs text-slate-500 flex items-start gap-2 bg-slate-50 p-2 rounded">
            <Wallet className="h-3.5 w-3.5 mt-0.5 text-slate-400" />
            <span>Statements close on the 30th of each month and are due on the 10th of the following month. Invoices for this customer will auto-flag as "on account" with payment due in {form.credit_terms_days} days.</span>
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !selectedCustomer} className="bg-slate-900 hover:bg-slate-800" data-testid="submit-account-btn">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Create account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
